import { useState, useEffect } from "react";
import { Menu, X, Sun, Moon, Globe, User, LogOut, ChevronDown, Info, PanelLeft, Globe2 } from "lucide-react"; // ✅ Tambahan icon User, LogOut, dan PanelLeft
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AuthButton from "@/components/auth/AuthButton";
import { useGlobalLoading } from "@/context/LoadingContext";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next"; // Tambahkan ini
import { useSidebarContext } from "@/components/private/layout/SidebarContext";

// ✅ Tambahan import untuk Dropdown dan Manajemen Sesi
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/context/useUser";
import { logout } from "@/services/auth";
import { setLoggingOut } from "@/lib/axiosClient";
import toast from "react-hot-toast";

export function Navigation() {
  const { t, i18n } = useTranslation("navigation"); // Gunakan namespace 'nav'
  const { isLoading } = useGlobalLoading();
  const [isDark, setIsDark] = useState(false);
  const navigate = useNavigate();
  const { toggleMobileSidebar, isMobileOpen } = useSidebarContext();

  // ✅ Ambil data user dari context
  const { data: user } = useUser();
  const [logoutLoading, setLogoutLoading] = useState(false);

  const toggleLanguage = () => {
    const newLanguage = i18n.language === "id" ? "en" : "id";
    i18n.changeLanguage(newLanguage);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    // Hanya aktifkan dark mode JIKA user pernah klik dan nyimpan "dark" di localStorage
    if (savedTheme === "dark") {
      setIsDark(true);
      document.documentElement.classList.add("dark");
    } else {
      // Default selalu light jika belum ada data di localStorage
      setIsDark(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // useEffect(() => {
  //   const savedTheme = localStorage.getItem("theme");
  //   const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  //   if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
  //     setIsDark(true);
  //     document.documentElement.classList.add("dark");
  //   }
  // }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  // ✅ Fungsi Logout Terpusat
  const handleLogout = async () => {
    setLogoutLoading(true);
    setLoggingOut(true);
    const success = await logout();
    setLoggingOut(false);
    setLogoutLoading(false);

    if (success) {
      toast.success(t('common:messages.success_logout') || "Berhasil logout");
      navigate("/login");
    } else {
      toast.error(t('common:messages.error_logout') || "Gagal logout");
    }
  };

  // ✅ HELPER: Fungsi untuk mengambil 2 kata pertama dari nama lengkap
  const getTwoWords = (fullName?: string) => {
    if (!fullName) return t('navigation:sidebar.default_user', 'User');
    const words = fullName.trim().split(/\s+/);
    if (words.length >= 2) {
      return `${words[0]} ${words[1]}`;
    }
    return words[0]; // Jika cuma 1 kata, kembalikan 1 kata
  };

  return (
    <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      {/* Progress Bar */}
      <div
        className={cn(
          "absolute top-0 left-0 h-[3px] bg-blue-500 transition-all duration-300",
          isLoading ? "w-full opacity-100" : "w-0 opacity-0"
        )}
      />
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            {/* ✅ Tombol Toggle Sidebar (Hanya tampil di Mobile lg:hidden, di desktop sudah dikontrol lewat sidebar & tombol floating) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileSidebar}
              className="lg:hidden w-9 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white transition-colors shrink-0 rounded-lg flex items-center justify-center cursor-pointer"
              title={t('navigation:sidebar.open_nav_menu', 'Buka Menu Navigasi')}
            >
              <PanelLeft className="h-5 w-5" />
            </Button>

            <div className="flex items-center space-x-3 cursor-pointer select-none" onClick={() => navigate("/dashboard")} >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center shrink-0">
                <img src={"/konaspi.png"} alt={t('navigation:logo_alt', 'Logo Konaspi')} className="w-12 h-12 md:w-14 md:h-14 object-contain" />
              </div>
              <div>
                <h1 className="text-lg md:text-xl font-bold text-foreground leading-tight">{t("title")}</h1>
                <p className="text-xs md:text-sm text-muted-foreground">{t("subtitle")}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">

            {/* Tombol ke Situs Publik - ikon di mobile, ikon+label di desktop */}
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              title={t('navigation:sidebar.menu.public_site', 'Situs Publik')}
              className="w-9 h-9 lg:w-auto lg:h-9 lg:px-4 lg:rounded-full text-sm font-medium gap-2 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors shrink-0 rounded-lg"
            >
              <Globe2 className="h-4 w-4 mb-1" />
              <span className="hidden lg:inline">{t('navigation:sidebar.menu.public_site', 'Situs Publik')}</span>
            </Button>

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="hidden lg:flex w-15 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:text-slate-200 dark:hover:text-white shrink-0 rounded-full"
            >
              <Globe className="h-4 w-4 mb-1" />
              <span className="ml-1 mb-1 text-xs font-medium">{i18n.language.toUpperCase()}</span>
            </Button>

            {/* ✅ MENU USER DROPDOWN ATAU AUTH BUTTON JIKA BELUM LOGIN */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="outline-none focus:ring-0 ml-1 md:ml-2 hidden lg:flex">
                  <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full  dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-slate-700 dark:text-slate-200 cursor-pointer select-none">
                    <User className="h-4 w-4 mb-1 shrink-0 opacity-70" />
                    <span className="font-medium text-xs md:text-sm whitespace-nowrap">
                      {t('navigation:sidebar.greeting', 'Halo')}, {getTwoWords(user.name)}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                  </div>
                </DropdownMenuTrigger>

                {/* ✅ PERBAIKAN: Gunakan z-[9999] agar popup melayang di atas segalanya */}
                <DropdownMenuContent align="end" className="w-64 mt-2 rounded-xl p-2 shadow-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-[9999]">
                  <div className="flex items-center justify-start gap-3 p-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-lg shrink-0">
                      {user.name ? user.name.charAt(0).toUpperCase() : <User className="h-5 w-5" />}
                    </div>
                    <div className="flex flex-col space-y-0.5 leading-none overflow-hidden">
                      <p className="font-bold text-sm text-slate-900 dark:text-white truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800 my-1" />
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer py-3 rounded-lg focus:bg-slate-50 dark:focus:bg-slate-800 transition-colors">
                    <User className="mr-3 h-4 w-4 text-slate-500" />
                    <span className="font-medium text-slate-700 dark:text-slate-300">{t('navigation:sidebar.menu.profile') || "Profil Saya"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer py-3 rounded-lg focus:bg-slate-50 dark:focus:bg-slate-800 transition-colors hidden lg:flex">
                    {isDark ? <Sun className="mr-3 h-4 w-4 text-slate-500" /> : <Moon className="mr-3 h-4 w-4 text-slate-500" />}
                    <span className="font-medium text-slate-700 dark:text-slate-300">{isDark ? t('navigation:sidebar.menu.is_light_mode') : t('navigation:sidebar.menu.is_dark_mode')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/")} className="cursor-pointer py-3 rounded-lg focus:bg-slate-50 dark:focus:bg-slate-800 transition-colors">
                    <Globe2 className="mr-3 h-4 w-4 text-slate-500" />
                    <span className="font-medium text-slate-700 dark:text-slate-300">{t('navigation:sidebar.menu.public_site', 'Situs Publik')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/about")} className="cursor-pointer py-3 rounded-lg focus:bg-slate-50 dark:focus:bg-slate-800 transition-colors">
                    <Info className="mr-3 h-4 w-4 text-slate-500" />
                    <span className="font-medium text-slate-700 dark:text-slate-300">{t('navigation:sidebar.menu.about') || "Tentang"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800 my-1" />
                  <DropdownMenuItem onClick={handleLogout} disabled={logoutLoading} className="cursor-pointer py-3 rounded-lg text-red-600 focus:text-red-700 focus:bg-red-50 dark:text-red-400 dark:focus:bg-red-950/30 font-bold transition-colors">
                    <LogOut className="mr-3 h-4 w-4" />
                    <span>{logoutLoading ? t("sidebar.loading", "Memuat...") : t("common:buttons.logout", "Logout")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                {/* Tampilan jika belum login */}
                <div className="lg:hidden shrink-0"><AuthButton checkAuth={true} iconOnly={true} /></div>
                <div className="hidden lg:flex shrink-0"><AuthButton checkAuth={true} /></div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="hidden lg:flex w-9 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white shrink-0"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden w-9 h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white transition-colors shrink-0 rounded-lg"
              onClick={toggleMobileSidebar}
            >
              {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
